"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BookmarkSyncService } from '@/services/BookmarkSyncService';

// Bookmark data structure
export interface Bookmark {
  id: string;
  title: string;
  url: string;
  category: string;
  description?: string;
  iconUrl?: string;
  addedAt: number;
}

// Context interface
interface BookmarkContextType {
  bookmarks: Bookmark[];
  categories: string[];
  isLoading: boolean;
  syncId: string | null;
  isSyncing: boolean;
  syncError: string | null;
  syncSuccess: string | null;
  addBookmark: (bookmark: Omit<Bookmark, 'id' | 'addedAt'>) => Promise<void>;
  removeBookmark: (id: string) => Promise<void>;
  updateBookmark: (id: string, updates: Partial<Omit<Bookmark, 'id' | 'addedAt'>>) => Promise<void>;
  getBookmarksByCategory: (category: string) => Bookmark[];
  isBookmarked: (url: string) => boolean;
  clearAllBookmarks: () => Promise<void>;
  importBookmarks: (importedBookmarks: Bookmark[]) => Promise<{ added: number; duplicates: number }>;
  exportBookmarks: () => Bookmark[];
  generateSyncId: () => Promise<string>;
  syncBookmarks: () => Promise<boolean>;
  syncBookmarksWithId: (syncId: string) => Promise<boolean>;
  clearSyncId: () => void;
}

// Create the context with default values
const BookmarkContext = createContext<BookmarkContextType>({
  bookmarks: [],
  categories: [],
  isLoading: false,
  syncId: null,
  isSyncing: false,
  syncError: null,
  syncSuccess: null,
  addBookmark: async () => { },
  removeBookmark: async () => { },
  updateBookmark: async () => { },
  getBookmarksByCategory: () => [],
  isBookmarked: () => false,
  clearAllBookmarks: async () => { },
  importBookmarks: async () => ({ added: 0, duplicates: 0 }),
  exportBookmarks: () => [],
  generateSyncId: async () => "",
  syncBookmarks: async () => false,
  syncBookmarksWithId: async () => false,
  clearSyncId: () => { },
});

// Simplified provider that handles SSR safely
export function BookmarkProvider({ children }: { children: ReactNode }) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [syncId, setSyncId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  // Mark that we're mounted on the client
  useEffect(() => {
    setMounted(true);

    // Load bookmarks from localStorage
    if (typeof window !== 'undefined') {
      setIsLoading(true);
      try {
        // Load bookmarks
        const savedBookmarks = localStorage.getItem('tannenboden_bookmarks');
        if (savedBookmarks) {
          const parsedBookmarks = JSON.parse(savedBookmarks);
          setBookmarks(parsedBookmarks);

          // Extract unique categories
          const uniqueCategories = [...new Set(parsedBookmarks.map((b: Bookmark) => b.category))] as string[];
          setCategories(uniqueCategories);
        }

        // Load saved sync ID
        const savedSyncId = localStorage.getItem('tannenboden_syncId');
        if (savedSyncId) {
          setSyncId(savedSyncId);
        }
      } catch (error) {
        console.error('Error loading bookmarks:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, []);
  // Generate a sync ID and save it
  const generateSyncId = async (): Promise<string> => {
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const newSyncId = BookmarkSyncService.generateSyncId();

      // Save to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('tannenboden_syncId', newSyncId);
      }

      setSyncId(newSyncId);

      // Upload current bookmarks with the new ID
      await BookmarkSyncService.saveBookmarks(newSyncId, bookmarks);

      setSyncSuccess('Sync ID generated successfully. Your bookmarks are now backed up to the cloud.');
      return newSyncId;
    } catch (error) {
      console.error('Error generating sync ID:', error);
      setSyncError('Failed to generate sync ID. Please try again.');
      return '';
    }
  };

  // Sync current bookmarks to the cloud using the stored sync ID
  const syncBookmarks = async (): Promise<boolean> => {
    if (!syncId) {
      setSyncError('No sync ID available. Please generate a sync ID first.');
      return false;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const success = await BookmarkSyncService.saveBookmarks(syncId, bookmarks);
      if (success) {
        setSyncSuccess('Bookmarks synced successfully.');
        setIsSyncing(false);
        return true;
      } else {
        throw new Error('Failed to sync bookmarks');
      }
    } catch (error) {
      console.error('Error syncing bookmarks:', error);
      setSyncError('Failed to sync bookmarks. Please try again.');
      setIsSyncing(false);
      return false;
    }
  };

  // Sync with a specific ID - either download remote bookmarks or upload current ones
  const syncBookmarksWithId = async (id: string): Promise<boolean> => {
    if (!BookmarkSyncService.validateSyncId(id)) {
      setSyncError('Invalid sync ID format. Sync IDs are 8 characters long.');
      return false;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      // Try to fetch remote bookmarks
      const remoteBookmarks = await BookmarkSyncService.loadBookmarks(id);

      if (remoteBookmarks) {
        // Remote bookmarks exist, import them
        await importBookmarks(remoteBookmarks);

        // Save the sync ID
        localStorage.setItem('tannenboden_syncId', id);
        setSyncId(id);

        setSyncSuccess('Bookmarks loaded successfully from cloud storage.');
        setIsSyncing(false);
        return true;
      } else {
        // No remote bookmarks, upload current bookmarks
        const success = await BookmarkSyncService.saveBookmarks(id, bookmarks);

        if (success) {
          // Save the sync ID
          localStorage.setItem('tannenboden_syncId', id);
          setSyncId(id);

          setSyncSuccess('No existing bookmarks found for this ID. Your current bookmarks have been synced.');
          setIsSyncing(false);
          return true;
        } else {
          throw new Error('Failed to save bookmarks for this ID');
        }
      }
    } catch (error) {
      console.error('Error syncing with ID:', error);
      setSyncError('Failed to sync with this ID. Please try again.');
      setIsSyncing(false);
      return false;
    }
  };

  // Clear the current sync ID
  const clearSyncId = () => {
    localStorage.removeItem('tannenboden_syncId');
    setSyncId(null);
    setSyncSuccess('Sync ID cleared. Your bookmarks are now only stored locally.');
  };
  // Generate a unique ID
  const generateId = () => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
  };

  // Save bookmarks to localStorage
  const saveBookmarks = (newBookmarks: Bookmark[]) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('tannenboden_bookmarks', JSON.stringify(newBookmarks));
      } catch (error) {
        console.error('Error saving bookmarks:', error);
      }
    }
  };

  // Add a new bookmark
  const addBookmark = async (bookmark: Omit<Bookmark, 'id' | 'addedAt'>) => {
    if (bookmarks.length >= 33) {
      throw new Error('Maximum number of bookmarks (33) reached');
    }

    if (bookmarks.some(b => b.url === bookmark.url)) {
      throw new Error('This URL is already bookmarked');
    }

    const newBookmark: Bookmark = {
      ...bookmark,
      id: generateId(),
      addedAt: Date.now(),
    };

    const updatedBookmarks = [...bookmarks, newBookmark];
    setBookmarks(updatedBookmarks);
    saveBookmarks(updatedBookmarks);

    // Update categories
    const newCategory = bookmark.category;
    if (!categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
    }
  };

  // Remove a bookmark by ID
  const removeBookmark = async (id: string) => {
    const updatedBookmarks = bookmarks.filter(b => b.id !== id);
    setBookmarks(updatedBookmarks);
    saveBookmarks(updatedBookmarks);

    // Update categories
    const remainingCategories = [...new Set(updatedBookmarks.map(b => b.category))] as string[];
    setCategories(remainingCategories);
  };

  // Update a bookmark
  const updateBookmark = async (id: string, updates: Partial<Omit<Bookmark, 'id' | 'addedAt'>>) => {
    const updatedBookmarks = bookmarks.map(bookmark =>
      bookmark.id === id ? { ...bookmark, ...updates } : bookmark
    );

    setBookmarks(updatedBookmarks);
    saveBookmarks(updatedBookmarks);

    // Update categories if needed
    if (updates.category) {
      const uniqueCategories = [...new Set(updatedBookmarks.map(b => b.category))] as string[];
      setCategories(uniqueCategories);
    }
  };

  // Get bookmarks by category
  const getBookmarksByCategory = (category: string): Bookmark[] => {
    return bookmarks.filter(b => b.category === category);
  };

  // Check if a URL is already bookmarked
  const isBookmarked = (url: string): boolean => {
    return bookmarks.some(b => b.url === url);
  };

  // Clear all bookmarks
  const clearAllBookmarks = async () => {
    setBookmarks([]);
    setCategories([]);
    saveBookmarks([]);
  };

  // Export bookmarks - returns the current bookmarks array
  const exportBookmarks = () => {
    return bookmarks;
  };

  // Import bookmarks from an external source
  const importBookmarks = async (importedBookmarks: Bookmark[]): Promise<{ added: number; duplicates: number }> => {
    if (!importedBookmarks || !Array.isArray(importedBookmarks) || importedBookmarks.length === 0) {
      throw new Error('Invalid bookmark data');
    }

    // Track how many were added vs duplicates
    let added = 0;
    let duplicates = 0;

    // Filter existing URLs to prevent duplicates
    const existingUrls = bookmarks.map(b => b.url);
    const newBookmarks: Bookmark[] = [];

    // Process each imported bookmark
    importedBookmarks.forEach(importedBookmark => {
      // Skip if total would exceed 33
      if (bookmarks.length + newBookmarks.length >= 33) {
        return;
      }

      // Check for duplicates by URL
      if (existingUrls.includes(importedBookmark.url)) {
        duplicates++;
        return;
      }

      // Ensure the bookmark has all required fields
      if (importedBookmark.title && importedBookmark.url && importedBookmark.category) {
        // Create a new bookmark with a new ID to prevent ID conflicts
        const newBookmark: Bookmark = {
          id: generateId(),
          title: importedBookmark.title,
          url: importedBookmark.url,
          category: importedBookmark.category,
          description: importedBookmark.description || '',
          iconUrl: importedBookmark.iconUrl,
          addedAt: importedBookmark.addedAt || Date.now(),
        };

        newBookmarks.push(newBookmark);
        added++;
      }
    });

    if (newBookmarks.length > 0) {
      const updatedBookmarks = [...bookmarks, ...newBookmarks];
      setBookmarks(updatedBookmarks);
      saveBookmarks(updatedBookmarks);

      // Update categories
      const uniqueCategories = [...new Set(updatedBookmarks.map(b => b.category))] as string[];
      setCategories(uniqueCategories);
    }

    return { added, duplicates };
  };

  // Always provide consistent structure between server and client
  // This helps prevent hydration errors
  const contextValue = {
    bookmarks: mounted ? [...bookmarks].sort((a, b) => b.addedAt - a.addedAt) : [],
    categories: mounted ? categories : [],
    isLoading,
    syncId,
    isSyncing,
    syncError,
    syncSuccess,
    addBookmark: mounted ? addBookmark : async () => { },
    removeBookmark: mounted ? removeBookmark : async () => { },
    updateBookmark: mounted ? updateBookmark : async () => { },
    getBookmarksByCategory: mounted ? getBookmarksByCategory : () => [],
    isBookmarked: mounted ? isBookmarked : () => false,
    clearAllBookmarks: mounted ? clearAllBookmarks : async () => { },
    importBookmarks: mounted ? importBookmarks : async () => ({ added: 0, duplicates: 0 }),
    exportBookmarks: mounted ? exportBookmarks : () => [],
    generateSyncId: mounted ? generateSyncId : async () => "",
    syncBookmarks: mounted ? syncBookmarks : async () => false,
    syncBookmarksWithId: mounted ? syncBookmarksWithId : async () => false,
    clearSyncId: mounted ? clearSyncId : () => { },
  };

  return (
    <BookmarkContext.Provider value={contextValue}>
      {children}
    </BookmarkContext.Provider>
  );
}

// Custom hook to use the bookmark context
export const useBookmarks = () => {
  const context = useContext(BookmarkContext);

  // Safety check
  if (typeof window === 'undefined') {
    // Return default values during SSR
    return {
      bookmarks: [],
      categories: [],
      isLoading: false,
      syncId: null,
      isSyncing: false,
      syncError: null,
      syncSuccess: null,
      addBookmark: async () => { },
      removeBookmark: async () => { },
      updateBookmark: async () => { },
      getBookmarksByCategory: () => [],
      isBookmarked: () => false,
      clearAllBookmarks: async () => { },
      importBookmarks: async () => ({ added: 0, duplicates: 0 }),
      exportBookmarks: () => [],
      generateSyncId: async () => "",
      syncBookmarks: async () => false,
      syncBookmarksWithId: async () => false,
      clearSyncId: () => { },
    };
  }

  return context;
};

export default BookmarkContext;